import React, { useState, useEffect, useCallback } from "react";
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { institutionAPI } from "../../../services/api";
import Table from "../../common/Table";
import toast from "react-hot-toast";

export default function ManageCollaborationType({ isOpen, onClose, onRefresh }) {
  const [typeName, setTypeName] = useState("");
  const [description, setDescription] = useState("");
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState("");

  // Fetch collaboration types
  const fetchTypes = useCallback(
    async (page = 1, perPage = pagination.perPage) => {
      try {
        setLoading(true);
        const res = await institutionAPI.getCollaborationTypes({ page, per_page: perPage });

        if (res.data.success) {
          const data = res.data.data?.collaboration_types || [];
          const meta = res.data.data?.meta || {};

          setTypes(data.map((c) => ({ ...c, id: c.id })));

          setPagination({
            currentPage: Number(meta.current_page) || page,
            lastPage: Number(meta.last_page) || 1,
            perPage: Number(meta.per_page) || perPage,
            total: Number(meta.total) || data.length,
          });
        }
      } catch (err) {
        console.error("Error fetching collaboration types:", err);
      } finally {
        setLoading(false);
      }
    },
    [pagination.perPage]
  );

  useEffect(() => {
    if (isOpen) fetchTypes(1);
  }, [isOpen, fetchTypes]);

  // Add new collaboration type
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!typeName.trim()) return;

    try {
      setLoading(true);
      const res = await institutionAPI.createCollaborationType({
        name: typeName,
        description: description || null,
      });

      if (res.data.success) {
        setTypeName("");
        setDescription("");
        fetchTypes(1);
        onRefresh?.();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to add collaboration type");
    } finally {
      setLoading(false);
    }
  };

  // Update collaboration type
  const handleUpdate = async (id) => {
    if (!editValue.trim()) return;

    try {
      const res = await institutionAPI.updateCollaborationType(id, {
        name: editValue,
        description: editDescription || null,
      });

      if (res.data.success) {
        setEditingId(null);
        setEditValue("");
        setEditDescription("");
        fetchTypes(pagination.currentPage);
        onRefresh?.();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to update collaboration type");
    }
  };

  // Trigger delete modal
  const confirmDelete = (id, name) => {
    setDeleteId(id);
    setDeleteName(name);
    setShowDeleteModal(true);
  };

  // Delete collaboration type
  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const res = await institutionAPI.deleteCollaborationType(deleteId);

      if (res.data.success) {
        toast.success("Collaboration type deleted");

        const newPage =
          types.length === 1 && pagination.currentPage > 1
            ? pagination.currentPage - 1
            : pagination.currentPage;

        fetchTypes(newPage);
        onRefresh?.();
        setShowDeleteModal(false);
        setDeleteId(null);
        setDeleteName("");
      }
    } catch (err) {
      console.error(err);

      if (
        err.response?.data?.message?.toLowerCase().includes("used") ||
        err.response?.data?.message?.toLowerCase().includes("associated") ||
        err.response?.status === 409
      ) {
        toast.error("Cannot delete — this collaboration type is already used");
      } else {
        toast.error(err.response?.data?.message || "Failed to delete collaboration type");
      }

      setShowDeleteModal(false);
    }
  };

  const columns = [
    {
      key: "name",
      label: <span className="text-xs font-medium">COLLABORATION TYPE</span>,
      render: (_, item) => (
        <div className="py-1 text-sm">
          {editingId === item.id ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-full border border-blue-400 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdate(item.id);
                if (e.key === "Escape") setEditingId(null);
              }}
            />
          ) : (
            <span className="text-gray-900 text-sm">{item.name}</span>
          )}
        </div>
      ),
    },
    {
      key: "description",
      label: <span className="text-xs font-medium">DESCRIPTION</span>,
      render: (_, item) => (
        <div className="py-1 text-sm">
          {editingId === item.id ? (
            <input
              type="text"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full border border-blue-400 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-blue-500 text-sm"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdate(item.id);
                if (e.key === "Escape") setEditingId(null);
              }}
            />
          ) : (
            <span className="text-gray-900 text-sm">{item.description || "—"}</span>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      label: <span className="text-xs font-medium">ACTIONS</span>,
      render: (_, item) => (
        <div className="flex gap-3">
          {editingId === item.id ? (
            <>
              <button
                onClick={() => handleUpdate(item.id)}
                className="text-green-600 hover:text-green-700 text-sm"
              >
                <CheckIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setEditingId(null)}
                className="text-red-500 hover:text-red-600 text-sm"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setEditingId(item.id);
                  setEditValue(item.name);
                  setEditDescription(item.description || "");
                }}
                className="text-yellow-500 hover:text-yellow-600 text-sm"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => confirmDelete(item.id, item.name)}
                className="text-red-500 hover:text-red-600 text-sm"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-center overflow-y-auto py-10">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl w-full max-w-4xl shadow-2xl p-6 my-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 border-b pb-4">
          <h2 className="text-xl font-bold text-gray-800 text-sm">Manage Collaboration Types</h2>
          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Add New */}
        <form onSubmit={handleAdd} className="mb-6 p-4 rounded-lg">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Name of Collaboration Type
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              placeholder="Enter collaboration type"
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            />
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optional)"
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium text-sm"
            >
              {loading ? "Adding..." : "Add"}
            </button>
          </div>
        </form>

        {/* Table */}
        <Table
          noContainer={true}
          columns={columns}
          data={types}
          getRowKey={(item) => item.id}
          pagination={pagination}
          onPageChange={(page) => fetchTypes(page, pagination.perPage)}
          paginationProps={{
            showSizeSelector: true,
            isLoading: loading,
            onPerPageChange: (size) => fetchTypes(1, size),
          }}
        />

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md text-sm">
              <h3 className="text-lg font-bold mb-4">Delete Collaboration Type</h3>
              <p className="mb-6">
                Are you sure you want to delete <strong>{deleteName}</strong>?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
